(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./ai-chat-core.js'), null);
    return;
  }

  if (root) {
    root.MarineMaxAIChatMarkdown = factory(root.MarineMaxAIChatCore, root);
  }
})(typeof window !== 'undefined' ? window : null, function (core, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the Markdown module');
  }

  const allowedHtmlTags = new Set([
    'A',
    'BLOCKQUOTE',
    'BR',
    'CODE',
    'DEL',
    'EM',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'HR',
    'INPUT',
    'LI',
    'OL',
    'P',
    'PRE',
    'STRONG',
    'TABLE',
    'TBODY',
    'TD',
    'TFOOT',
    'TH',
    'THEAD',
    'TR',
    'UL',
  ]);
  const blockedHtmlTags = new Set([
    'AUDIO',
    'BUTTON',
    'EMBED',
    'FORM',
    'IFRAME',
    'IMG',
    'MATH',
    'OBJECT',
    'OPTION',
    'SCRIPT',
    'SELECT',
    'STYLE',
    'SVG',
    'TEMPLATE',
    'TEXTAREA',
    'VIDEO',
  ]);
  const allowedLinkProtocols = new Set(['http:', 'https:', 'mailto:', 'tel:']);

  function formatInlineMarkdown(value) {
    let html = core.escapeHtml(value);

    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    );
    html = html.replace(
      /(^|[\s(])((https?:\/\/[^\s<)]+))/g,
      function (_match, prefix, url) {
        return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      },
    );
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/(^|[^\*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    return html;
  }

  function fallbackMarkdownToHtml(markdown) {
    const source = String(markdown == null ? '' : markdown).replace(/\r\n?/g, '\n');
    const lines = source.split('\n');
    const html = [];
    let paragraphLines = [];
    let listType = '';
    let listItems = [];

    function flushParagraph() {
      if (!paragraphLines.length) return;
      const paragraphHtml = paragraphLines.map((line) => formatInlineMarkdown(line)).join('<br>');
      html.push(`<p>${paragraphHtml}</p>`);
      paragraphLines = [];
    }

    function flushList() {
      if (!listType || !listItems.length) {
        listType = '';
        listItems = [];
        return;
      }
      html.push(
        `<${listType}>${listItems
          .map((item) => `<li>${formatInlineMarkdown(item)}</li>`)
          .join('')}</${listType}>`,
      );
      listType = '';
      listItems = [];
    }

    lines.forEach((rawLine) => {
      const line = String(rawLine || '');
      const trimmed = line.trim();

      if (!trimmed) {
        flushParagraph();
        flushList();
        return;
      }

      const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        flushParagraph();
        flushList();
        const level = Math.min(6, headingMatch[1].length);
        html.push(`<h${level}>${formatInlineMarkdown(headingMatch[2])}</h${level}>`);
        return;
      }

      const unorderedMatch = trimmed.match(/^[-*+]\s+(.+)$/);
      if (unorderedMatch) {
        flushParagraph();
        if (listType && listType !== 'ul') flushList();
        listType = 'ul';
        listItems.push(unorderedMatch[1]);
        return;
      }

      const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
      if (orderedMatch) {
        flushParagraph();
        if (listType && listType !== 'ol') flushList();
        listType = 'ol';
        listItems.push(orderedMatch[1]);
        return;
      }

      flushList();
      paragraphLines.push(trimmed);
    });

    flushParagraph();
    flushList();

    return html.join('');
  }

  function isAllowedLink(href, origin) {
    const value = String(href || '').trim();
    if (!value) return false;
    if (value.startsWith('#')) return true;

    try {
      const url = new URL(value, origin || 'https://www.marinemax.com');
      return allowedLinkProtocols.has(url.protocol);
    } catch (_) {
      return false;
    }
  }

  function sanitizeHtml(html, options = {}) {
    const documentRef = options.document || (root && root.document);
    if (!documentRef) {
      throw new Error('A document is required to sanitize AI Chat HTML');
    }

    const origin = options.origin || (root && root.location ? root.location.origin : '');
    const template = documentRef.createElement('template');
    template.innerHTML = String(html == null ? '' : html);

    template.content.querySelectorAll('*').forEach((element) => {
      if (!allowedHtmlTags.has(element.tagName)) {
        if (blockedHtmlTags.has(element.tagName)) {
          element.remove();
        } else {
          element.replaceWith(...element.childNodes);
        }
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        const allowedForElement =
          (element.tagName === 'A' && ['href', 'rel', 'target', 'title'].includes(attributeName)) ||
          (element.tagName === 'OL' && attributeName === 'start') ||
          (['LI', 'UL'].includes(element.tagName) && attributeName === 'class') ||
          (element.tagName === 'INPUT' && ['checked', 'disabled', 'type'].includes(attributeName)) ||
          (['TD', 'TH'].includes(element.tagName) && attributeName === 'align');

        if (!allowedForElement) {
          element.removeAttribute(attribute.name);
        }
      });

      if (element.tagName === 'A') {
        const href = element.getAttribute('href');
        if (href && !isAllowedLink(href, origin)) {
          element.removeAttribute('href');
        }
        if (element.getAttribute('target') === '_blank') {
          element.setAttribute('rel', 'noopener noreferrer');
        } else if (element.hasAttribute('target') && element.getAttribute('target') !== '_self') {
          element.removeAttribute('target');
        }
      }

      if (element.tagName === 'INPUT') {
        if (element.getAttribute('type') !== 'checkbox') {
          element.remove();
          return;
        }
        element.setAttribute('disabled', '');
      }

      if (['LI', 'UL'].includes(element.tagName) && element.hasAttribute('class')) {
        const safeClasses = String(element.getAttribute('class') || '')
          .split(/\s+/)
          .filter((className) => ['contains-task-list', 'task-list-item'].includes(className));
        if (safeClasses.length) {
          element.setAttribute('class', safeClasses.join(' '));
        } else {
          element.removeAttribute('class');
        }
      }
    });

    return template.innerHTML;
  }

  function renderMarkdown(markdown, options = {}) {
    const markedApi = options.marked || (root && root.marked);
    if (markedApi && typeof markedApi.parse === 'function') {
      try {
        return sanitizeHtml(
          markedApi.parse(String(markdown == null ? '' : markdown), {
            breaks: true,
            gfm: true,
          }),
          options,
        );
      } catch (_) {}
    }

    return sanitizeHtml(fallbackMarkdownToHtml(markdown), options);
  }

  return Object.freeze({
    fallbackMarkdownToHtml,
    formatInlineMarkdown,
    isAllowedLink,
    renderMarkdown,
    sanitizeHtml,
  });
});
